import { motion } from 'framer-motion';
import { PILLARS, ENVIRONMENTS } from '@/lib/index';
import { ArrowDown, Mail, Leaf, Droplets, Wind } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const pillarIcons = [
  <Droplets size={28} className="text-primary" />,
  <Leaf size={28} className="text-accent" />,
  <Wind size={28} className="text-primary" />,
];

export default function Home() {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="w-full">
      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              "url('/images/caribbean_turquoise_ocean_water_saint_ma_1.jpeg')",
          }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-background/70" />
        {/* Gradient overlay bottom */}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-background to-transparent" />

        {/* Content */}
        <motion.div
          className="relative z-10 text-center px-6 max-w-3xl mx-auto"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.div
            variants={fadeInUp}
            className="flex flex-col items-center gap-1 mb-6"
            style={{ textShadow: '0 1px 12px rgba(0,0,0,0.8)' }}
          >
            <p className="text-sm tracking-[0.35em] text-white font-medium uppercase">
              Saint-Martin
            </p>
            <p className="text-sm tracking-[0.35em] text-white/80 font-normal uppercase">
              Sint Maarten
            </p>
            <p className="text-xs tracking-[0.35em] text-white/60 uppercase mt-0.5">
              Caribbean Islands
            </p>
          </motion.div>

          <motion.h1
            variants={fadeInUp}
            style={{ fontFamily: "'Special Elite', serif" }}
            className="text-5xl md:text-7xl font-medium tracking-wide text-foreground mb-6 leading-tight"
          >
            Gaia Rise
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            className="text-base md:text-lg tracking-[0.2em] text-muted-foreground uppercase mb-4"
          >
            Conscious Living Protocol
          </motion.p>

          <motion.p
            variants={fadeInUp}
            className="text-base md:text-lg text-foreground/80 leading-relaxed max-w-xl mx-auto mb-12"
          >
            A protocol for those who choose to live differently.
            <br />
            Water. Purity. Earth.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => scrollTo('protocol')}
              className="px-8 py-3.5 bg-primary text-primary-foreground text-sm tracking-widest uppercase rounded-sm hover:bg-primary/90 transition-all duration-300"
            >
              Discover the Protocol
            </button>
            <button
              onClick={() => scrollTo('contact')}
              className="px-8 py-3.5 border border-border text-foreground text-sm tracking-widest uppercase rounded-sm hover:bg-card/40 transition-all duration-300"
            >
              Begin the Conversation
            </button>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.button
          onClick={() => scrollTo('protocol')}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 text-muted-foreground hover:text-foreground transition-colors"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          aria-label="Scroll down"
        >
          <ArrowDown size={20} />
        </motion.button>
      </section>

      {/* ─── PROTOCOL ─────────────────────────────────────────── */}
      <section id="protocol" className="py-28 px-6">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs tracking-[0.35em] text-primary font-semibold uppercase mb-4"
            >
              What is Gaia Rise
            </motion.p>

            <motion.h2
              variants={fadeInUp}
              style={{ fontFamily: "'Special Elite', serif" }}
              className="text-4xl md:text-5xl font-medium text-foreground mb-8 leading-tight"
            >
              A living protocol.
              <br />
              Not a certification.
            </motion.h2>

            <motion.div
              variants={fadeInUp}
              className="space-y-5 text-base text-muted-foreground leading-relaxed text-left"
            >
              <p>
                Gaia Rise was born from a simple frustration: in the most beautiful places in the world — the Caribbean, the Mediterranean, the Pacific — the standards of living are still deeply toxic. We clean with chemicals. We drink processed water. We breathe recycled air. We throw everything away.
              </p>
              <p>
                It didn't have to be this way.
              </p>
              <p>
                Gaia Rise is a conscious living protocol for those environments where people expect the highest quality of life: yachts, catamarans, private villas, boutique restaurants. It's a curated set of practices, products, and partners — built from real experience, tested in real conditions, operating in real places.
              </p>
              <p>
                We are currently piloting in Saint Martin, Caribbean.{' '}
                <span className="text-foreground">We are building this with intention, not haste.</span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── DIVIDER ──────────────────────────────────────────── */}
      <div className="container mx-auto max-w-3xl px-6">
        <div className="border-t border-border/30" />
      </div>

      {/* ─── THREE PILLARS ────────────────────────────────────── */}
      <section id="pillars" className="py-28 px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs tracking-[0.35em] text-primary font-semibold uppercase mb-4 text-center"
            >
              The Framework
            </motion.p>
            <motion.h2
              variants={fadeInUp}
              style={{ fontFamily: "'Special Elite', serif" }}
              className="text-4xl md:text-5xl font-medium text-foreground mb-16 text-center leading-tight"
            >
              Three Pillars
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8">
              {PILLARS.map((pillar, idx) => (
                <motion.div
                  key={pillar.number}
                  variants={fadeInUp}
                  className="bg-card border border-border/40 rounded-lg p-8 flex flex-col gap-5 hover:border-primary/40 transition-all duration-300"
                  style={{
                    boxShadow: '0 4px 24px -8px color-mix(in srgb, var(--primary) 10%, transparent)',
                  }}
                >
                  <div className="flex items-center gap-4">
                    <span
                      style={{ fontFamily: "'Special Elite', serif" }}
                      className="text-4xl text-primary/60 font-medium leading-none"
                    >
                      {pillar.number}
                    </span>
                    {pillarIcons[idx]}
                  </div>

                  <div>
                    <h3
                      style={{ fontFamily: "'Special Elite', serif" }}
                      className="text-xl font-medium text-foreground mb-1"
                    >
                      {pillar.title}
                    </h3>
                    <p className="text-xs text-primary font-medium italic tracking-wide">{pillar.subtitle}</p>
                  </div>

                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                    {pillar.description}
                  </p>

                  <ul className="space-y-2">
                    {pillar.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <span className="text-accent mt-0.5">◆</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── ENVIRONMENTS ─────────────────────────────────────── */}
      <section className="py-20 px-6 bg-card/50">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs tracking-[0.35em] text-primary font-semibold uppercase mb-4 text-center"
            >
              Where We Operate
            </motion.p>
            <motion.h2
              variants={fadeInUp}
              style={{ fontFamily: "'Special Elite', serif" }}
              className="text-3xl md:text-4xl font-medium text-foreground mb-12 text-center"
            >
              Four Pilot Environments
            </motion.h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {ENVIRONMENTS.map((env) => (
                <motion.div
                  key={env.title}
                  variants={fadeInUp}
                  className="text-center p-6 bg-background rounded-lg border border-border/30 hover:border-primary/30 transition-all duration-300"
                >
                  <span className="text-3xl block mb-3">{env.icon}</span>
                  <h4 className="text-sm font-medium text-foreground tracking-wide mb-2">{env.title}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{env.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── ABOUT ────────────────────────────────────────────── */}
      <section id="about" className="py-28 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-16 items-center"
          >
            {/* Photo side */}
            <motion.div variants={fadeInUp} className="relative">
              <div
                className="w-full aspect-[3/4] bg-cover bg-center rounded-sm overflow-hidden"
                style={{
                  backgroundImage:
                    "url('/images/clean_water_glass_nature_sustainable_liv_6.jpeg')",
                }}
              />
              <div
                className="absolute -bottom-4 -right-4 w-24 h-24 border border-primary/20 rounded-sm"
                style={{ background: 'transparent' }}
              />
            </motion.div>

            {/* Text side */}
            <motion.div variants={fadeInUp} className="flex flex-col gap-6">
              <div>
                <p className="text-sm tracking-[0.35em] text-primary font-bold uppercase mb-4">
                  The Founder
                </p>
                <h2
                  style={{ fontFamily: "'Special Elite', serif" }}
                  className="text-4xl font-medium text-foreground mb-4"
                >
                  Silvia Dalu
                </h2>
                <p className="text-sm tracking-widest text-foreground font-medium uppercase">
                  20 years in hospitality, land and sea, worldwide.
                  <br />
                  Based in the Caribbean for 13 years.
                </p>
              </div>

              <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
                <p>
                  I spent twenty years creating beautiful experiences for other people — on yachts crossing the Atlantic, in villas overlooking the Caribbean, at tables where every detail was considered. And for years I didn't see the contradiction.
                </p>
                <p>
                  The cleaners I used. The water guests drank. The products that touched their skin. All of it, quietly toxic. All of it, quietly ending up in the ocean I lived beside.
                </p>
                <p>
                  Gaia Rise is my answer to that contradiction. Not an abstract idea — a real, practical protocol built from everything I know about how people actually live in these environments.
                </p>
              </div>

              <div className="pt-2">
                <a
                  href="mailto:gaiarise.collective@proton.me"
                  className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  <Mail size={14} />
                  <span className="tracking-wider">Write to me directly</span>
                </a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── CONTACT / CTA ────────────────────────────────────── */}
      <section
        id="contact"
        className="relative py-28 px-6 overflow-hidden"
      >
        {/* Background water image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('/images/clean_water_glass_nature_sustainable_liv_7.jpeg')",
          }}
        />
        <div className="absolute inset-0 bg-background/80" />

        <div className="relative z-10 container mx-auto max-w-2xl text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs tracking-[0.35em] text-primary font-semibold uppercase mb-6"
            >
              Join Us
            </motion.p>

            <motion.h2
              variants={fadeInUp}
              style={{ fontFamily: "'Special Elite', serif" }}
              className="text-4xl md:text-5xl font-medium text-foreground mb-6 leading-tight"
            >
              We're looking for allies,
              <br />
              not just partners.
            </motion.h2>

            <motion.p
              variants={fadeInUp}
              className="text-base text-muted-foreground leading-relaxed mb-10 max-w-lg mx-auto"
            >
              Are you a small brand with a real product and real values? A property owner in Saint Martin who wants to do this differently? A scientist or practitioner working in the field of water, air, or soil? 
              <br /><br />
              We want to hear from you.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:gaiarise.collective@proton.me"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary text-primary-foreground text-sm tracking-widest uppercase rounded-sm hover:bg-primary/90 transition-all duration-300"
              >
                <Mail size={14} />
                Begin the Conversation
              </a>
              <a
                href="https://gaiarise.eco"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-3.5 border border-border text-foreground text-sm tracking-widest uppercase rounded-sm hover:bg-card/40 transition-all duration-300"
              >
                gaiarise.eco
              </a>
            </motion.div>

            <motion.p
              variants={fadeInUp}
              className="text-xs text-muted-foreground mt-10 tracking-wide"
            >
              Currently active in Saint Martin, Caribbean.
              <br />
              Building with care. Not in a hurry.
            </motion.p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
